<template>
  <div class="flex-1 flex flex-col overflow-hidden bg-white p-3">

    <div class="grid grid-cols-6 gap-2 py-2">
      <input
        v-model="search"
        @change="searchEntity"
        type="text"
        placeholder="Search by Name or BRN"
        class="col-span-2 w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" />
      <select v-model="type" @change="fetch"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary">
        <option :value="null">Filter by Type</option>
        <option value="private-company-limited-by-shares">Investment Club</option>
        <option value="single-member-company">Association for Personal Growth</option>
      </select>
      <select v-model="status" @change="fetch"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary">
        <option :value="null">Filter by Status</option>
        <option value="Registered">Registered</option>
        <option value="Insolvent">Insolvent</option>
        <option value="Ceased">Ceased</option>
      </select>
    </div>

    <div class="bg-white shadow overflow-hidden flex flex-col">
      <div class="overflow-x-auto flex-1">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 border-b border-gray-100 sticky top-0">
          <tr>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">#</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">BRN</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Sub Entity Type</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Reg. Date</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
          </tr>
          </thead>
          <tbody>
          <template v-if="!loading && entities.length > 0">
            <tr v-for="(entity, idx) in entities" :key="idx"
                class="border-b border-gray-100 hover:bg-gray-50 transition">
              <td class="py-3 px-4 font-bold">{{ (currentPage - 1) * limit + idx + 1 }}</td>
              <td class="py-3 px-4 font-bold text-primary">{{ entity.brn }}</td>
              <td class="py-3 px-4 font-bold">{{ entity.name }}</td>
              <td class="py-3 px-4">{{ entity.subType ? cleanEntityType(entity.subType) : "—" }}</td>
              <td class="py-3 px-4">{{ dateFormat(entity.dateOfRegistration) }}</td>
              <td class="py-3 px-4">
                  <span
                    class="text-xs font-medium px-3 py-1 rounded-full inline-block text-green-700 font-bold bg-green-100">
                    Registered
                  </span>
              </td>
              <td class="py-3 px-4">
                <button @click="viewFile(entity.brn)" class="btn-sm-primary" title="View File">
                  <i class="fa-solid fa-eye"></i> View File
                </button>
              </td>
            </tr>
          </template>
          <tr v-if="loading">
            <td colspan="7" class="py-16 text-center">
              <i class="fa fa-spinner fa-spin text-primary text-2xl"></i>
            </td>
          </tr>
          <template v-if="!loading && entities.length === 0">
            <tr>
              <td colspan="7" class="py-16 text-center">
                <div class="flex flex-col items-center justify-center text-gray-400">
                  <i class="fa fa-folder-open text-6xl mb-4"></i>
                  <p class="text-lg font-medium text-gray-500">No Building Societies Found</p>
                  <p class="text-sm text-gray-400 mt-2">No registered building societies in the registry.</p>
                </div>
              </td>
            </tr>
          </template>
          </tbody>
        </table>
      </div>
      <Pagination :pagination="pagination" :page="currentPage" :limit="limit" label="building societies"
                  @update="update" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { cleanEntityType, dateFormat } from "@/commons/composables/utilities.ts";
import { useBusinessRegistryStore } from "@/stores/business-registry.ts";
import Pagination from "@/commons/components/Pagination.vue";
import type { Pagination as PaginationType } from "@/app/types";

interface Entity {
  brn: string;
  name: string;
  subType?: string;
  dateOfRegistration: string;
}

const registryStore = useBusinessRegistryStore();
const router = useRouter();

const loading = ref(false);
const currentPage = ref(1);
const limit = ref(15);
const entities = ref<Entity[]>([]);
const pagination = ref<PaginationType | undefined>(undefined);
const search = ref("");
const status = ref<string | null>(null);
const type = ref<string | null>(null);

onMounted(() => {
  fetch();
});

const fetch = () => {
  loading.value = true;
  registryStore.fetchBuildingSocietyRegistry({
    search: search.value,
    page: currentPage.value - 1,
    limit: limit.value,
    offset: limit.value
  });
};

const searchEntity = () => {
  loading.value = true;
  fetch();
};

const viewFile = (brn: string) => {
  localStorage.setItem("entityType", "building-society");
  router.push("/home/non-individuals/file-viewer/" + brn);
  router.go(0);
};

function update(pg: number) {
  currentPage.value = pg;
  fetch();
}

watch(() => registryStore.building_society_registry, (data) => {
  if (data) {
    loading.value = false;
    entities.value = data.content || [];
    pagination.value = {
      currentPage: currentPage.value,
      nextPage: currentPage.value + 1,
      totalRows: data.totalElements || 0,
      totalPages: data.totalPages || 0
    };
  }
});

watch(() => registryStore.error, (data) => {
  if (data) loading.value = false;
});
</script>
